import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { redirectTo, selectCurrentUser, setAuthLoading, setUser } from "../../store/auth.duck";
import { PUBLIC_ROUTES } from "@app/App";
import { rawUserRequest } from '@shared/services/Auth';
import {
  AuthType,
  clearLoginStorage,
  GetCurrentAuthType,
  HandleAuthenticationResult,
  LoginWithRedirect,
  resetAllLoginStorages,
} from '@shared/utils/AuthUtils';
import { useMsal } from "@azure/msal-react";
import { AuthenticationResult, IPublicClientApplication } from "@azure/msal-browser";
import { microsoftRequest } from '@shared/services/EnvironmentVariables';
import { setPostLoginRedirect } from '@shared/utils/UrlUtils';
import { TrackJS } from 'trackjs';
import { bootstrapAuth, requestCalendarAccess } from '@shared/auth/authMachine.duck';


export function WithWebAuth() {
  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const {instance} = useMsal();
  const instanceRef = useRef<undefined | IPublicClientApplication>(undefined);

  // Check current token
  //  This will also clear auth loading
  useEffect(() => {
    const trySilentMsLogin = async () => {
      try {
        const accounts = instance.getAllAccounts();
        if (!instance.getActiveAccount() && accounts.length > 0) {
          instance.setActiveAccount(accounts[0]);
        }

        const result: AuthenticationResult = await instance.acquireTokenSilent({
          ...microsoftRequest.login,
          redirectUri: `${window.location.origin}/blank.html`,
          forceRefresh: false,
        });

        await HandleAuthenticationResult(result, dispatch, true);

      } catch (e: any) {
        if ((e?.message ?? '').toLowerCase().includes('user not registered')) {
          clearLoginStorage();
          dispatch(redirectTo('/login'))
        } else {
          await LoginWithRedirect(instance, dispatch, true);
        }
      }
    }

    const checkCurrentToken = async () => {
      try {
        if (GetCurrentAuthType() === AuthType.Microsoft && hasMsalAccount(instance)) {
          await (dispatch(bootstrapAuth({ source: 'app-mount' })) as any).unwrap();
          const userResp = await rawUserRequest();
          dispatch(setUser(userResp));
        } else if (hasNonMsalTokens()) {
          const user = await rawUserRequest();
          dispatch(setUser(user));
        }
      } catch (e: any) {
        console.error(e)
        if (e.message.toLowerCase().includes('azure access token timed out')) {
          await trySilentMsLogin();
        } else {
          TrackJS?.track(e);
          resetAllLoginStorages();
          dispatch(redirectTo('/login'));
        }
      } finally {
        dispatch(setAuthLoading(false));
      }
    }

    const hasAuth = hasMsalAccount(instance) || hasNonMsalTokens();

    if (!PUBLIC_ROUTES.includes(window.location.pathname) && !hasAuth) {
      // Remember the protected page they were trying to reach so we can return them
      // to it after they log in (preserving any query string / hash).
      setPostLoginRedirect(window.location.pathname + window.location.search + window.location.hash);
      dispatch(redirectTo('/login'));
      dispatch(setAuthLoading(false));
    } else if (['/login/azure', '/register/azure'].includes(window.location.pathname)) {
      dispatch(setAuthLoading(false));
    } else {
      checkCurrentToken();
    }
  }, [dispatch, instance]);

  useEffect(() => {
    instanceRef.current = instance;
  }, [instance])

  useEffect(() => {
    if (user?.companyEntity?.calendarSyncEnabled && user?.enableOutlookSync && user?.authType !== AuthType.Google) {
      dispatch(requestCalendarAccess({ source: 'auto' }));
    }
  }, [user, dispatch])

}

function hasMsalAccount(instance: IPublicClientApplication): boolean {
  return instance.getAllAccounts().length > 0 || !!instance.getActiveAccount();
}

function hasNonMsalTokens(): boolean {
  const authType = GetCurrentAuthType();
  if (authType === AuthType.Standard) return !!localStorage.getItem('idToken');
  if (authType === AuthType.Google) return !!localStorage.getItem('accessToken');
  return false;
}
